/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import {
    ArrowRight,
    ExternalLink,
    KeyRound,
    Link2,
    ShieldCheck,
    Zap,
} from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import PublicFooter from '@/client/components/marketing/PublicFooter';
import PublicHeader from '@/client/components/marketing/PublicHeader';
import {
    AFFILIATES_API_DOCS_CANONICAL_PATH,
    AFFILIATES_API_DOCS_CONTENT,
    AFFILIATES_API_DOCS_LANGUAGES,
    AFFILIATES_API_DOCS_PREVIEW_MODES,
    buildAffiliatesApiAuthSnippet,
    buildAffiliatesApiCurlSnippet,
    buildAffiliatesApiJavaScriptSnippet,
    buildAffiliatesApiRequestTypings,
    buildAffiliatesApiRedemptionSnippet,
    buildAffiliatesApiRequestExample,
    buildAffiliatesApiResponseExample,
    buildAffiliatesApiResponseTypings,
    formatAffiliatesApiJson,
    type AffiliatesApiDocCodeLanguage,
    type AffiliatesApiDocField,
    type AffiliatesApiDocFieldLocation,
    type AffiliatesApiDocPreviewMode,
    type AffiliatesApiDocSection,
    type AffiliatesApiDocSectionId,
} from '@/client/catalogs/marketing/api/pageContent';
import { buildContactHref } from '@/client/catalogs/marketing/navigation';
import useContext from '@/client/context';
import Page from '@/client/pages/_layout/_page';
import { getRecruitersAppUrl } from '@/common/config/router';
import useHeader from '@client/pages/useHeader';
import CopyableCodeBlock from './CopyableCodeBlock';

/*----------------------------------
- TYPES
----------------------------------*/

export interface AffiliatesApiDocumentationPageProps {
    currentOrigin: string;
    isLegacyAlias?: boolean;
}

/*----------------------------------
- CONSTANTS
----------------------------------*/

const LOCATION_LABELS: Record<AffiliatesApiDocFieldLocation, string> = {
    header: 'Header',
    body: 'Body',
    path: 'Path',
    response: 'Response',
};

const AFFILIATES_API_CONTACT_HREF = buildContactHref({
    topic: 'api-access',
    source: 'affiliates-api',
});

/*----------------------------------
- HELPERS
----------------------------------*/

const getSectionIdFromHash = (hash: string): AffiliatesApiDocSectionId | null => {
    const normalized = hash.replace(/^#/, '').trim();
    if (!normalized) return null;

    const match = AFFILIATES_API_DOCS_CONTENT.sections.find((section) => section.id === normalized);
    return match?.id || null;
};

const getSectionIcon = (sectionId: AffiliatesApiDocSectionId) => {
    switch (sectionId) {
        case 'overview':
            return ShieldCheck;
        case 'generate-signup-link':
            return KeyRound;
        case 'redemption-flow':
            return Link2;
    }
};

const renderFieldRows = (fields: readonly AffiliatesApiDocField[]) => {
    return fields.map((field) => (
        <div
            key={`${field.location}:${field.name}`}
            className="grid gap-3 border-t border-slate-200 px-5 py-4 first:border-t-0 md:grid-cols-[180px_120px_minmax(0,1fr)]"
        >
            <div>
                <p className="text-sm font-semibold text-slate-900">{field.name}</p>
                <p className="mt-1 text-xs text-slate-500">{LOCATION_LABELS[field.location]}</p>
            </div>
            <div className="flex flex-col gap-2">
                <span className="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {field.type}
                </span>
                <span
                    className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                        field.required
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-emerald-100 text-emerald-700'
                    }`}
                >
                    {field.required ? 'Required' : 'Optional'}
                </span>
            </div>
            <p className="text-sm leading-6 text-slate-600">{field.description}</p>
        </div>
    ));
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function AffiliatesApiDocumentationPage({
    currentOrigin,
    isLegacyAlias = false,
}: AffiliatesApiDocumentationPageProps) {
    const { page } = useContext();

    useHeader({
        title: 'Affiliates API',
        description:
            'Affiliate-facing API documentation for creating assigned signup links and redeeming CrossPath packs without a separate purchase step.',
    });

    const recruitersAppBaseUrl = React.useMemo(() => getRecruitersAppUrl(currentOrigin), [currentOrigin]);
    const canonicalHref = React.useMemo(() => {
        return new URL(AFFILIATES_API_DOCS_CANONICAL_PATH, currentOrigin).toString();
    }, [currentOrigin]);

    if (page && isLegacyAlias) {
        const hasCanonicalOverride = page.head.some((entry) => {
            return entry.$ === 'link' && String(entry.rel || '') === 'canonical' && String(entry.href || '') === canonicalHref;
        });

        if (!hasCanonicalOverride) {
            page.head.push({
                $: 'link',
                rel: 'canonical',
                href: canonicalHref,
            });
        }
    }

    const [selectedSectionId, setSelectedSectionId] = React.useState<AffiliatesApiDocSectionId>('overview');
    const [selectedLanguage, setSelectedLanguage] = React.useState<AffiliatesApiDocCodeLanguage>('curl');
    const [selectedPreviewMode, setSelectedPreviewMode] = React.useState<AffiliatesApiDocPreviewMode>('example');

    const selectedSection = React.useMemo<AffiliatesApiDocSection>(() => {
        return (
            AFFILIATES_API_DOCS_CONTENT.sections.find((section) => section.id === selectedSectionId)
            || AFFILIATES_API_DOCS_CONTENT.sections[0]
        );
    }, [selectedSectionId]);

    const requestPreview = React.useMemo(() => {
        if (selectedSection.id !== 'generate-signup-link') return '';

        return selectedPreviewMode === 'typings'
            ? buildAffiliatesApiRequestTypings()
            : formatAffiliatesApiJson(buildAffiliatesApiRequestExample());
    }, [selectedPreviewMode, selectedSection.id]);

    const responsePreview = React.useMemo(() => {
        if (selectedSection.id === 'generate-signup-link') {
            return selectedPreviewMode === 'typings'
                ? buildAffiliatesApiResponseTypings('generate-signup-link')
                : formatAffiliatesApiJson(buildAffiliatesApiResponseExample(recruitersAppBaseUrl));
        }

        if (selectedSection.id === 'redemption-flow') {
            return selectedPreviewMode === 'typings'
                ? buildAffiliatesApiResponseTypings('redemption-flow')
                : formatAffiliatesApiJson({
                        signupUrl: `${recruitersAppBaseUrl}/partner-signup/psg_demo_token_01`,
                        anonymousVisitorRedirect: `${recruitersAppBaseUrl}/auth/signup/:templatePackId`,
                        redeemedVisitorRedirect: `${recruitersAppBaseUrl}/auth/login/:templatePackId`,
                        outcome: 'Successful auth activates the assigned pack without a separate purchase step.',
                    });
        }

        return '';
    }, [recruitersAppBaseUrl, selectedPreviewMode, selectedSection.id]);

    const activeSnippet = React.useMemo(() => {
        if (selectedSection.id === 'generate-signup-link') {
            return selectedLanguage === 'curl'
                ? buildAffiliatesApiCurlSnippet(recruitersAppBaseUrl)
                : buildAffiliatesApiJavaScriptSnippet(recruitersAppBaseUrl);
        }

        if (selectedSection.id === 'redemption-flow') {
            return buildAffiliatesApiRedemptionSnippet(recruitersAppBaseUrl);
        }

        return buildAffiliatesApiAuthSnippet();
    }, [recruitersAppBaseUrl, selectedLanguage, selectedSection.id]);

    const activeSnippetLanguage = React.useMemo(() => {
        if (selectedSection.id !== 'generate-signup-link') return 'text';

        return selectedLanguage === 'javascript' ? 'typescript' : 'bash';
    }, [selectedLanguage, selectedSection.id]);

    const previewLanguage = selectedPreviewMode === 'typings' ? 'typescript' : 'json';

    const activeSnippetCaption = React.useMemo(() => {
        if (selectedSection.id === 'generate-signup-link') {
            return selectedLanguage === 'curl'
                ? 'Server-to-server request example'
                : 'JavaScript backend request example';
        }

        if (selectedSection.id === 'redemption-flow') {
            return 'What the returned browser URL does';
        }

        return 'Header contract used by the Affiliates API endpoint';
    }, [selectedLanguage, selectedSection.id]);

    const activeSnippetTitle = React.useMemo(() => {
        if (selectedSection.id === 'generate-signup-link') {
            return selectedLanguage === 'curl' ? 'cURL example' : 'JavaScript fetch example';
        }

        if (selectedSection.id === 'redemption-flow') {
            return 'Redemption behavior';
        }

        return 'Authentication headers';
    }, [selectedLanguage, selectedSection.id]);

    const hasJsonPreview = Boolean(requestPreview || responsePreview);

    React.useEffect(() => {
        const hashSectionId = getSectionIdFromHash(window.location.hash);
        if (hashSectionId) {
            setSelectedSectionId(hashSectionId);
        }
    }, []);

    React.useEffect(() => {
        if (!isLegacyAlias) return;

        const canonicalLinks = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
        if (!canonicalLinks.length) return;

        canonicalLinks[0].setAttribute('href', canonicalHref);

        for (let index = canonicalLinks.length - 1; index >= 1; index -= 1) {
            canonicalLinks[index].remove();
        }
    }, [canonicalHref, isLegacyAlias]);

    const handleSelectSection = React.useCallback((sectionId: AffiliatesApiDocSectionId) => {
        setSelectedSectionId(sectionId);
        window.history.replaceState(null, '', `#${sectionId}`);
    }, []);

    return (
        <Page withLayout={false}>
            <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(251,146,60,0.18),_transparent_32%),linear-gradient(180deg,_#fff7ed_0%,_#ffffff_34%,_#fff1f2_100%)]" data-testid="affiliates-api-page">
                <PublicHeader currentPage="partners" ctaLabel="Get API Access" ctaHref={AFFILIATES_API_CONTACT_HREF} />

                <section className="px-6 pb-16 pt-32 lg:px-8 lg:pb-20">
                    <div className="mx-auto max-w-7xl">
                        <div className="max-w-4xl">
                            <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-white/85 px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm">
                                <Zap size={16} />
                                {AFFILIATES_API_DOCS_CONTENT.hero.eyebrow}
                            </div>
                            <h1 className="mt-6 max-w-4xl text-5xl font-bold tracking-tight text-slate-950 md:text-6xl">
                                {AFFILIATES_API_DOCS_CONTENT.hero.title}
                            </h1>
                            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
                                {AFFILIATES_API_DOCS_CONTENT.hero.description}
                            </p>
                            <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                                <a
                                    href={AFFILIATES_API_CONTACT_HREF}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-4 text-base font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:from-orange-600 hover:to-pink-600"
                                >
                                    Get API Access
                                    <ArrowRight size={18} />
                                </a>
                                <a
                                    href={recruitersAppBaseUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:bg-slate-50"
                                >
                                    Open recruiters app
                                    <ExternalLink size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="px-6 pb-20 lg:px-8 lg:pb-24">
                    <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
                        <aside className="lg:sticky lg:top-28 lg:self-start">
                            <div className="rounded-3xl border border-orange-200/80 bg-white/90 p-5 shadow-lg shadow-orange-100/60">
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Sections</p>
                                <div className="mt-4 space-y-2">
                                    {AFFILIATES_API_DOCS_CONTENT.sections.map((section) => {
                                        const Icon = getSectionIcon(section.id);
                                        const isActive = section.id === selectedSectionId;

                                        return (
                                            <button
                                                key={section.id}
                                                type="button"
                                                onClick={() => handleSelectSection(section.id)}
                                                data-testid={`affiliates-api-nav-${section.id}`}
                                                className={`w-full rounded-2xl border px-4 py-4 text-left transition ${
                                                    isActive
                                                        ? 'border-orange-300 bg-orange-50 text-orange-700 shadow-sm'
                                                        : 'border-transparent bg-slate-50 text-slate-600 hover:border-slate-200 hover:bg-white'
                                                }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`rounded-xl p-2 ${isActive ? 'bg-orange-100' : 'bg-white'}`}>
                                                        <Icon size={18} />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold">{section.label}</p>
                                                        <p className="mt-1 text-xs leading-5 text-slate-500">{section.eyebrow}</p>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-950 p-5 text-slate-100 shadow-xl">
                                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">Flow</p>
                                <div className="mt-4 space-y-4">
                                    {AFFILIATES_API_DOCS_CONTENT.flowSteps.map((step, index) => (
                                        <div key={step.title} className="flex gap-3">
                                            <span className="mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
                                                {index + 1}
                                            </span>
                                            <div>
                                                <p className="text-sm font-semibold text-white">{step.title}</p>
                                                <p className="mt-1 text-xs leading-5 text-slate-300">{step.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </aside>

                        <div className="space-y-8">
                            <div className="overflow-hidden rounded-[28px] border border-white/70 bg-white/95 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.65)]">
                                <div className="border-b border-slate-200 px-6 py-6 sm:px-8">
                                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-orange-600">
                                        {selectedSection.eyebrow}
                                    </p>
                                    <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                        <div>
                                            <h2 className="text-3xl font-bold tracking-tight text-slate-950" data-testid="affiliates-api-panel-title">
                                                {selectedSection.title}
                                            </h2>
                                            <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                                                {selectedSection.summary}
                                            </p>
                                        </div>
                                        {selectedSection.method && selectedSection.path ? (
                                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-right">
                                                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                                                    Endpoint
                                                </p>
                                                <p className="mt-2 text-sm font-semibold text-orange-700">{selectedSection.method}</p>
                                                <p className="mt-1 font-mono text-sm text-slate-800">{selectedSection.path}</p>
                                            </div>
                                        ) : null}
                                    </div>
                                    <p className="mt-5 max-w-3xl text-sm leading-6 text-slate-600">{selectedSection.description}</p>
                                    <ul className="mt-6 grid gap-3 sm:grid-cols-3">
                                        {selectedSection.bullets.map((bullet) => (
                                            <li key={bullet} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm leading-6 text-slate-700">
                                                {bullet}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {selectedSection.requestFields.length ? (
                                    <div className="border-b border-slate-200">
                                        <div className="px-6 py-5 sm:px-8">
                                            <h3 className="text-lg font-semibold text-slate-950">Request contract</h3>
                                            <p className="mt-1 text-sm text-slate-500">Headers, path parameters, and body fields used by this step.</p>
                                        </div>
                                        {renderFieldRows(selectedSection.requestFields)}
                                    </div>
                                ) : null}

                                {selectedSection.responseFields.length ? (
                                    <div>
                                        <div className="px-6 py-5 sm:px-8">
                                            <h3 className="text-lg font-semibold text-slate-950">Response contract</h3>
                                            <p className="mt-1 text-sm text-slate-500">The response fields returned by CrossPath for this call.</p>
                                        </div>
                                        {renderFieldRows(selectedSection.responseFields)}
                                    </div>
                                ) : null}

                                {selectedSection.id === 'redemption-flow' ? (
                                    <div className="border-t border-slate-200 px-6 py-6 sm:px-8">
                                        <h3 className="text-lg font-semibold text-slate-950">Redirect outcomes</h3>
                                        <div className="mt-4 grid gap-4 md:grid-cols-3">
                                            {AFFILIATES_API_DOCS_CONTENT.redemptionOutcomes.map((outcome) => (
                                                <div key={outcome.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                                    <p className="text-sm font-semibold text-slate-900">{outcome.title}</p>
                                                    <p className="mt-2 text-sm leading-6 text-slate-600">{outcome.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="space-y-6">
                                {selectedSection.id === 'generate-signup-link' || hasJsonPreview ? (
                                    <div className="rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-lg shadow-slate-100/70">
                                        {selectedSection.id === 'generate-signup-link' ? (
                                            <div>
                                                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Code examples</p>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {AFFILIATES_API_DOCS_LANGUAGES.map((language) => (
                                                        <button
                                                            key={language.id}
                                                            type="button"
                                                            onClick={() => setSelectedLanguage(language.id)}
                                                            data-testid={`affiliates-api-language-${language.id}`}
                                                            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                                                selectedLanguage === language.id
                                                                    ? 'bg-slate-950 text-white'
                                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                                            }`}
                                                        >
                                                            {language.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}

                                        {hasJsonPreview ? (
                                            <div className={selectedSection.id === 'generate-signup-link' ? 'mt-5 border-t border-slate-200 pt-5' : ''}>
                                                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Payload view</p>
                                                <div className="mt-3 flex flex-wrap gap-2">
                                                    {AFFILIATES_API_DOCS_PREVIEW_MODES.map((mode) => (
                                                        <button
                                                            key={mode.id}
                                                            type="button"
                                                            onClick={() => setSelectedPreviewMode(mode.id)}
                                                            data-testid={`affiliates-api-preview-${mode.id}`}
                                                            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                                                selectedPreviewMode === mode.id
                                                                    ? 'bg-orange-500 text-white'
                                                                    : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                                                            }`}
                                                        >
                                                            {mode.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}

                                <CopyableCodeBlock
                                    title={activeSnippetTitle}
                                    caption={activeSnippetCaption}
                                    code={activeSnippet}
                                    testId="affiliates-api-snippet"
                                    language={activeSnippetLanguage}
                                />

                                {requestPreview ? (
                                    <CopyableCodeBlock
                                        title={selectedPreviewMode === 'typings' ? 'TypeScript request typings' : 'JSON request example'}
                                        caption={selectedPreviewMode === 'typings'
                                            ? 'Typed request shape for POST /api/partners/signup-link'
                                            : 'The body sent to POST /api/partners/signup-link'}
                                        code={requestPreview}
                                        testId="affiliates-api-request"
                                        language={previewLanguage}
                                    />
                                ) : null}

                                {responsePreview ? (
                                    <CopyableCodeBlock
                                        title={selectedPreviewMode === 'typings' ? 'TypeScript response typings' : 'JSON response preview'}
                                        caption={selectedPreviewMode === 'typings'
                                            ? (selectedSection.id === 'generate-signup-link'
                                                ? 'Typed response shape returned by CrossPath'
                                                : 'Typed browser handoff contract returned by the redemption flow')
                                            : (selectedSection.id === 'generate-signup-link'
                                                ? 'Representative 200 response'
                                                : 'How the browser handoff behaves after signupUrl is opened')}
                                        code={responsePreview}
                                        testId="affiliates-api-response"
                                        language={previewLanguage}
                                    />
                                ) : null}
                            </div>

                            <div className="rounded-[28px] border border-orange-200 bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-8 text-white shadow-2xl shadow-orange-200/60 sm:px-8">
                                <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-100">
                                            Need access?
                                        </p>
                                        <h3 className="mt-3 text-2xl font-bold">Request the Affiliates API key and template pack setup.</h3>
                                        <p className="mt-3 max-w-2xl text-sm leading-6 text-orange-50">
                                            CrossPath will issue the affiliate technical account, the API key, and the template packs your integration should use when calling the signup-link endpoint.
                                        </p>
                                    </div>
                                    <a
                                        href={AFFILIATES_API_CONTACT_HREF}
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-orange-700 transition hover:-translate-y-0.5 hover:text-orange-800"
                                    >
                                        Get API Access
                                        <ArrowRight size={18} />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <PublicFooter tagline="Document one affiliate contract, then let customers redeem assigned packs without a purchase step." />
            </div>
        </Page>
    );
}
